<div>
  <div class="mx-auto">
    <div class="row g-3">
      <div class="col-12 col-xl-8">
        <section class="entity-binding-workspace bg-white bg-opacity-75 rounded-4 border shadow p-3 p-md-4 h-100">
          <div class="d-flex flex-wrap align-items-start justify-content-between gap-2 mb-4">
            <div>
              <h1 class="fs-5 fw-bold mb-1">Active Child Bindings</h1>
              <p class="entity-panel-caption small fw-semibold mb-0">
                Connect logical components and define relationship hierarchies for the
                {{ entity_type_name }} entity.
              </p>
            </div>
            <span class="entity-detail-pill entity-status-active rounded-pill fw-bold text-uppercase">{{ active_bindings_count }} Active</span>
          </div>

          <section class="bg-white rounded-4 border p-3 p-md-3 mb-4 shadow">
            <div class="d-flex align-items-start gap-2 mb-3">
              <div class="entity-new-binding-icon rounded-3 d-inline-flex align-items-center justify-content-center">
                <i class="bi bi-link-45deg"></i>
              </div>
              <div>
                <h2 class="fw-bold small mb-1">Add New Child</h2>
                <div class="entity-panel-caption small fw-semibold">
                  Configure automated child-entity assignment.
                </div>
              </div>
            </div>

            <div class="row g-3 align-items-end">
              <div class="col-12 col-lg-8">
                <label class="form-label mb-1 fw-semibold text-uppercase entity-binding-field-label">Select Child Entity</label>
                <select
                  v-model="selected_sub_entity_id"
                  class="form-select subsystem-select entity-binding-control rounded-small"
                  :disabled="sub_entities_loading"
                >
                  <option value="">
                    {{ sub_entities_loading ? 'Loading entity types...' : 'Select an entity type to bind' }}
                  </option>
                  <option
                    v-for="item in bindable_sub_entities"
                    :key="item.id"
                    :value="item.id"
                  >
                    {{ item.name }}
                  </option>
                </select>
              </div>

              <div class="col-12 col-lg-4">
                <button
                  type="button"
                  class="btn entity-create-binding-btn rounded-small shadow-sm px-3 py-2 fw-semibold border-0 text-white w-100"
                  :disabled="!selected_sub_entity_id || binding_sub_entity || sub_entities_loading"
                  @click="bind_sub_entity"
                >
                  <span v-if="binding_sub_entity">Binding...</span>
                  <template v-else>
                  <i class="bi bi-link-45deg me-2"></i>
                  Bind Child
                  </template>
                </button>
              </div>

              <div class="col-12">
                <div class="entity-panel-caption small fw-semibold">
                  Note: child entities help model hierarchy, ownership, and dependency relationships.
                </div>
              </div>
            </div>
          </section>

          <section>
            <div class="d-flex align-items-center justify-content-between gap-2 mb-3">
              <h2 class="fw-bold small mb-0">Current Bindings</h2>
              <div class="entity-panel-caption small fw-semibold">{{ bindings.length }} configured</div>
            </div>

            <div class="d-flex flex-column gap-3">
              <div
                v-if="bindings_loading"
                class="entity-activity-card rounded-4 p-4 small text-secondary bg-white"
              >
                Loading current bindings...
              </div>

              <article
                v-for="binding in bindings"
                :key="binding.id"
                class="entity-binding-list-card bg-white rounded-4 p-3 shadow-sm border border-light-subtle"
                role="button"
                tabindex="0"
                @click="open_child_entity_config(binding)"
                @keydown.enter.prevent="open_child_entity_config(binding)"
                @keydown.space.prevent="open_child_entity_config(binding)"
              >
                <div class="d-flex align-items-start justify-content-between gap-3 flex-wrap">
                  <div class="d-flex align-items-center gap-3 min-w-0 flex-grow-1">
                    <div class="entity-new-binding-icon rounded-3 d-inline-flex align-items-center justify-content-center flex-shrink-0 shadow-sm" style="width: 2.5rem; height: 2.5rem;">
                      <i class="bi bi-diagram-3-fill text-white"></i>
                    </div>

                    <div class="d-flex flex-column gap-2 min-w-0 flex-grow-1">
                      <div class="d-flex flex-wrap align-items-center gap-2 min-w-0">
                        <div class="fw-bold small mb-0 text-dark">{{ binding.name }}</div>
                      </div>

                      <div v-if="binding.description" class="small text-secondary text-truncate">
                        {{ binding.description }}
                      </div>

                      <div class="d-flex flex-wrap align-items-center gap-2">
                        <span class="small text-dark fw-semibold">
                          Configurations :
                        </span>
                        <span
                          v-for="item in binding.config_summary_items"
                          :key="`${binding.id}-${item.key}`"
                          class="badge rounded-pill border shadow-sm fw-semibold px-2 py-1 text-dark bg-white"
                        >
                          <i class="bi me-1" :class="item.icon"></i>
                          {{ item.label }}: {{ item.count }}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div class="d-flex flex-column align-items-end justify-content-between gap-3 ms-auto pt-1 h-100 align-self-stretch">
                    <span
                      class="badge rounded-pill fw-semibold px-2 py-1"
                      :class="binding.configuration_status_tone_class === 'text-success' ? 'bg-success-subtle text-success border border-success-subtle' : 'bg-danger-subtle text-danger border border-danger-subtle'"
                    >
                      <i class="bi me-1" :class="binding.configuration_status_icon"></i>
                      {{ binding.configuration_status_text }}
                    </span>
                    <div class="d-flex align-items-center gap-3 flex-wrap justify-content-end mt-auto">
                      <button
                        type="button"
                        class="btn btn-sm rounded-pill px-3 py-2 fw-semibold border-0 bg-white text-dark text-xs"
                        :disabled="unbinding_child_id === String(binding.id)"
                        @click.stop="remove_binding(binding)"
                      >
                        <template v-if="unbinding_child_id === String(binding.id)">
                          <span class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                          Removing...
                        </template>
                        <template v-else>
                          <i class="bi bi-x-lg me-1 text-dark"></i>
                          Remove
                        </template>
                      </button>
                    </div>
                  </div>
                </div>
              </article>

              <div
                v-if="!bindings_loading && !bindings.length"
                class="entity-activity-card rounded-4 p-4 small text-secondary bg-white"
              >
                No child bindings yet. Add the first child above.
              </div>
            </div>
          </section>
        </section>
      </div>

      <div class="col-12 col-xl-4">
        <aside class="entity-overview-profile-card rounded-4 border shadow p-4 h-100 overflow-hidden">
          <h2 class="entity-overview-title fw-bold mb-4">Child Entities Overview</h2>

          <div class="small mb-4">
            <div class="entity-panel-caption fw-semibold mb-1">Current context for</div>
            <div class="fw-bold">{{ entity_type_name }}</div>
            <div class="text-secondary mt-2">
              {{ current_entity?.entity_type_description || 'Manage how this entity connects to its child entities and requirement rules.' }}
            </div>
          </div>

          <div class="mb-4">
            <div class="text-uppercase entity-overview-eyebrow mb-2">Quick Summary</div>
            <div class="small text-dark">
              {{ active_bindings_count ? `${active_bindings_count} child ${active_bindings_count === 1 ? 'binding is' : 'bindings are'} currently linked to this entity.` : 'No child entities are linked yet.' }}
            </div>
          </div>

          <div class="entity-overview-divider my-4"></div>


          <div class="d-flex flex-column gap-2">
            <button
              type="button"
              class="btn entity-overview-profile-btn rounded-small w-100 small fw-semibold"
              @click="go_to_child_requirements"
            >
              <i class="bi bi-ui-checks-grid me-2"></i>
              View Child Requirements
            </button>

            <button
              type="button"
              class="btn entity-overview-profile-btn rounded-small w-100 small fw-semibold"
              @click="go_to_entity_overview"
            >
              <i class="bi bi-eye me-2"></i>
              View Full Profile
            </button>
          </div>
        </aside>
      </div>
    </div>
  </div>
</div>
